// RBAC API — extracted from api.js (Tier 2 #9a round 3).
// Org-level roles + groups + group membership, per-view RBAC permissions
// (record / detail / chart), reusable access templates, and the unified
// RBAC options endpoint that the UI uses to populate role/group pickers.

import api from './api';

// ─── Roles ─────────────────────────────────────────────────────────────────
export const fetchRoles = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/roles`);
  return response.data;
};

export const createRole = async (orgId, data) => {
  const response = await api.post(`/orgs/${orgId}/roles`, data);
  return response.data;
};

export const updateRole = async (orgId, roleId, data) => {
  const response = await api.put(`/orgs/${orgId}/roles/${roleId}`, data);
  return response.data;
};

export const deleteRole = async (orgId, roleId) => {
  const response = await api.delete(`/orgs/${orgId}/roles/${roleId}`);
  return response.data;
};

// fetchRoleImpact — preview of what will be affected by deleting this role.
// The delete is ON DELETE CASCADE at the FK level; this call lets the UI show
// the blast radius before confirming.
export const fetchRoleImpact = async (orgId, roleId) => {
  const response = await api.get(`/orgs/${orgId}/roles/${roleId}/impact`);
  return response.data;
};

// ─── Groups ────────────────────────────────────────────────────────────────
export const fetchGroups = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/groups`);
  return response.data;
};

export const createGroup = async (orgId, data) => {
  const response = await api.post(`/orgs/${orgId}/groups`, data);
  return response.data;
};

export const updateGroup = async (orgId, groupId, data) => {
  // `data` was historically just a name string; accept both shapes for compatibility.
  const payload = typeof data === 'string' ? { name: data } : data;
  const response = await api.put(`/orgs/${orgId}/groups/${groupId}`, payload);
  return response.data;
};

export const deleteGroup = async (orgId, groupId) => {
  const response = await api.delete(`/orgs/${orgId}/groups/${groupId}`);
  return response.data;
};

// fetchGroupImpact — same shape as fetchRoleImpact; see note there.
export const fetchGroupImpact = async (orgId, groupId) => {
  const response = await api.get(`/orgs/${orgId}/groups/${groupId}/impact`);
  return response.data;
};

// ─── Group Membership ──────────────────────────────────────────────────────
export const fetchGroupUsers = async (groupId) => {
  const response = await api.get(`/groups/${groupId}/users`);
  return response.data;
};

export const addUserToGroup = async (groupId, userId) => {
  const response = await api.post(`/groups/${groupId}/users`, {
    user_id: userId,
  });
  return response.data;
};

export const removeUserFromGroup = async (groupId, userId) => {
  const response = await api.delete(`/groups/${groupId}/users/${userId}`);
  return response.data;
};

// ─── Per-View RBAC Permissions ─────────────────────────────────────────────
// The access-gate that decides who can OPEN the view at all. Complements the
// row-level prefilters which are unchanged. Empty/missing permissions = open to all.
// Payload matches updateScreenPermissions — RBACPanel in mode="screen" works as-is.
export const fetchRecordViewPermissionsByUUID = async (appId, rvUid) => {
  const response = await api.get(`/apps/${appId}/record-views/${rvUid}/permissions`);
  return response.data;
};
export const updateRecordViewPermissionsByUUID = async (appId, rvUid, data) => {
  const response = await api.put(`/apps/${appId}/record-views/${rvUid}/permissions`, data);
  return response.data;
};

export const fetchDetailViewPermissionsByUUID = async (appId, dvUid) => {
  const response = await api.get(`/apps/${appId}/detail-views/${dvUid}/permissions`);
  return response.data;
};
export const updateDetailViewPermissionsByUUID = async (appId, dvUid, data) => {
  const response = await api.put(`/apps/${appId}/detail-views/${dvUid}/permissions`, data);
  return response.data;
};

export const fetchChartTemplatePermissionsByUUID = async (appId, ctUid) => {
  const response = await api.get(`/apps/${appId}/chart-templates/${ctUid}/permissions`);
  return response.data;
};
export const updateChartTemplatePermissionsByUUID = async (appId, ctUid, data) => {
  const response = await api.put(`/apps/${appId}/chart-templates/${ctUid}/permissions`, data);
  return response.data;
};

export const fetchActivityViewPermissionsByUUID = async (appId, avUid) => {
  const response = await api.get(`/apps/${appId}/activity-views/${avUid}/permissions`);
  return response.data;
};
export const updateActivityViewPermissionsByUUID = async (appId, avUid, data) => {
  const response = await api.put(`/apps/${appId}/activity-views/${avUid}/permissions`, data);
  return response.data;
};

export const fetchRbacOptions = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/rbac-options`);
  return response.data;
};

// ─── Access Templates (org-scoped RBAC presets) ────────────────────────────
// Activities reference a template by id; template edits propagate live (the renderer
// resolves on each RBAC check). Activities can still add extra roles/groups on top.
export const fetchAccessTemplates = async (orgId) => {
  const response = await api.get(`/orgs/${orgId}/access-templates`);
  return response.data;
};

export const fetchAccessTemplate = async (orgId, id) => {
  const response = await api.get(`/orgs/${orgId}/access-templates/${id}`);
  return response.data;
};

export const createAccessTemplate = async (orgId, data) => {
  const response = await api.post(`/orgs/${orgId}/access-templates`, data);
  return response.data;
};

export const updateAccessTemplate = async (orgId, id, data) => {
  const response = await api.put(`/orgs/${orgId}/access-templates/${id}`, data);
  return response.data;
};

export const deleteAccessTemplate = async (orgId, id) => {
  const response = await api.delete(`/orgs/${orgId}/access-templates/${id}`);
  return response.data;
};

export const fetchAccessTemplateUsages = async (orgId, id) => {
  const response = await api.get(
    `/orgs/${orgId}/access-templates/${id}/usages`,
  );
  return response.data;
};
